import {
  BrowserAttachmentBlobStore,
  openIndexedDbKnowledgeStore,
  type IndexedDbKnowledgeStoreOptions,
} from "@note-app/storage-indexeddb";

import {
  PlatformRuntimeError,
  unavailableCapability,
  type PlatformCapabilities,
} from "./capabilities.ts";
import { BrowserAttachmentPort } from "./browser-attachments.ts";
import { BrowserStorageHealthPort } from "./browser-storage.ts";
import { BrowserKnowledgeBundlePort } from "./notebundle.ts";
import type {
  CameraPort,
  ExportFileInput,
  FileSelectionPort,
  PlatformRuntime,
  SharePort,
} from "./ports.ts";

export interface BrowserRuntimeOptions extends IndexedDbKnowledgeStoreOptions {
  readonly platform?: "web" | "ios" | "android";
}

export async function createBrowserPlatformRuntime(
  options: BrowserRuntimeOptions = {},
): Promise<PlatformRuntime> {
  const store = await openIndexedDbKnowledgeStore(options);
  const blobStore = new BrowserAttachmentBlobStore({
    database: store.database(),
  });
  const attachmentBackend = await blobStore.storageBackend();
  const platform = options.platform ?? "web";
  const capabilities: PlatformCapabilities = {
    platform,
    structuredStorage: {
      support: store.readOnly ? "degraded" : "available",
      ...(store.readOnly
        ? { reason: "Future schema detected; writes are disabled." }
        : {}),
    },
    attachmentStorage:
      attachmentBackend === "opfs"
        ? { support: "available" }
        : {
            support: "degraded",
            reason: "OPFS is unavailable; attachments use quota-checked IndexedDB.",
          },
    files: { support: "available" },
    camera:
      platform === "web"
        ? {
            support: "degraded",
            reason: "Camera capture depends on browser file-input support.",
          }
        : {
            support: "unavailable",
            reason: "A native camera adapter has not been composed.",
          },
    share:
      typeof navigator.share === "function"
        ? { support: "available" }
        : {
            support: "degraded",
            reason: "System share is unavailable; copying remains available.",
          },
    installable:
      platform === "web"
        ? { support: "available" }
        : { support: "unavailable", reason: "Native apps are already installed." },
    backgroundSync: {
      support: "unavailable",
      reason: "No cloud account or synchronization transport is configured.",
    },
    complexCanvasEditing: {
      support: "available",
      reason:
        "The shared canvas supports touch and pointer authoring, autosave, references, images, and revision recovery.",
    },
  };
  const attachments = new BrowserAttachmentPort(
    store.attachments,
    blobStore,
  );
  return {
    platform,
    capabilities,
    unitOfWork: store,
    storageHealth: new BrowserStorageHealthPort(),
    bundles: new BrowserKnowledgeBundlePort(store, blobStore),
    attachments,
    files: new BrowserFileSelectionPort(),
    camera: new BrowserCameraPort(),
    share: new BrowserSharePort(),
    close() {
      attachments.releaseAll();
      store.close();
    },
  };
}

class BrowserFileSelectionPort implements FileSelectionPort {
  async pickNotebundle(signal?: AbortSignal): Promise<File | null> {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    return new Promise<File | null>((resolve, reject) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = ".notebundle,application/zip";
      input.hidden = true;
      const cleanup = () => {
        signal?.removeEventListener("abort", onAbort);
        input.remove();
      };
      const onAbort = () => {
        cleanup();
        reject(new DOMException("Cancelled", "AbortError"));
      };
      input.addEventListener("change", () => {
        const file = input.files?.[0] ?? null;
        cleanup();
        resolve(file);
      }, { once: true });
      signal?.addEventListener("abort", onAbort, { once: true });
      document.body.append(input);
      input.click();
    });
  }

  async exportFile(
    input: ExportFileInput,
    signal?: AbortSignal,
  ): Promise<void> {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    const bytes = input.bytes.buffer.slice(
      input.bytes.byteOffset,
      input.bytes.byteOffset + input.bytes.byteLength,
    ) as ArrayBuffer;
    const blob = new Blob([bytes], { type: input.mimeType });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = input.fileName;
    anchor.hidden = true;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

class BrowserCameraPort implements CameraPort {
  async capture(): Promise<never> {
    return unavailableCapability(
      "camera",
      "Direct camera capture is unavailable in this browser runtime. Use the attachment picker.",
    );
  }
}

class BrowserSharePort implements SharePort {
  async share(
    payload: import("./ports.ts").SharePayload,
    signal?: AbortSignal,
  ): Promise<void> {
    if (signal?.aborted) throw new DOMException("Cancelled", "AbortError");
    if (typeof navigator.share !== "function") {
      throw new PlatformRuntimeError(
        "unsupported",
        "System sharing is unavailable in this browser.",
        "share",
      );
    }
    await navigator.share({
      title: payload.title,
      ...(payload.text === undefined ? {} : { text: payload.text }),
      ...(payload.url === undefined ? {} : { url: payload.url }),
      ...(payload.files === undefined ? {} : { files: [...payload.files] }),
    });
  }
}
